import { IBaseTextBoxProps, BaseTextBox } from "./BaseTextBox";

import { IValidationCondition } from "./Validation/IValidationCondition";

import { IValidationRule } from "./Validation/IValidationRule";

import { isEmpty, isNumber } from "./Validation/RawValueRules";

import {
    hasLessDecimalPlacesThan,
    valueIsAtLeast,
    valueIsAtMost,
    valueIsLessThan,
    valueIsMoreThan,
} from "./Validation/NumericValueRules";
import { Content } from "./Resources/Content";

export interface INumberTextBoxSpecificProps extends IBaseTextBoxProps<number> {
    atLeast?: IValidationCondition<number>;
    atMost?: IValidationCondition<number>;
    lessThan?: IValidationCondition<number>;
    moreThan?: IValidationCondition<number>;
    maxNumberOfDecimalPlaces?: IValidationCondition<number>;
    validationRules?: IValidationRule<number>[];
    atMostSecondary?: IValidationCondition<number>;
    displayFormatCulture?: string;
    isCultureUsedToFormatDisplayValue?: boolean;
    allowDecimals?: boolean;
}

export interface INumberTextBoxProps extends INumberTextBoxSpecificProps, React.Props<NumberTextBox> {
    // NOTE: leave empty
}

export class NumberTextBox extends BaseTextBox<number, INumberTextBoxProps> {
    private static _formatCulture: string;

    constructor(props: INumberTextBoxProps) {
        super(props);
        NumberTextBox._formatCulture = this.props.displayFormatCulture || Globalize.culture().name;
    }

    protected isParsable(input: string): boolean {
        return isNumber(input, NumberTextBox._formatCulture);
    }

    protected parseValue(input: string): number {
        return isEmpty(input) ? null : Globalize.parseFloat(input, 10, NumberTextBox._formatCulture);
    }

    protected initRawValidation(): void {
        super.initRawValidation();

        this.validator.rawValidator.add(this.isParsable, Content.Value_must_be_a_number);

        if (NumberTextBox._formatCulture && this.props.maxNumberOfDecimalPlaces) {
            const decimalPlacesRule = (value: string) =>
                hasLessDecimalPlacesThan(
                    value,
                    this.props.maxNumberOfDecimalPlaces.value,
                    NumberTextBox._formatCulture
                );

            this.validator.rawValidator.add(decimalPlacesRule, this.props.maxNumberOfDecimalPlaces.errorMessage);
        } else {
            this.validator.rawValidator.addFromCondition(this.props.maxNumberOfDecimalPlaces, hasLessDecimalPlacesThan);
        }
    }

    protected initParsedValidation(): void {
        super.initParsedValidation();

        this.validator.parsedValidator.addFromCondition(this.props.atLeast, valueIsAtLeast);
        this.validator.parsedValidator.addFromCondition(this.props.atMost, valueIsAtMost);
        this.validator.parsedValidator.addFromCondition(this.props.atMostSecondary, valueIsAtMost);
        this.validator.parsedValidator.addFromCondition(this.props.lessThan, valueIsLessThan);
        this.validator.parsedValidator.addFromCondition(this.props.moreThan, valueIsMoreThan);

        this.validator.parsedValidator.addRange(this.props.validationRules);
    }

    protected getDisplayValue(): string {
        const value = this.state.value;
        const decimalSeparator = Globalize.culture().numberFormat["."];
        const lastSymbolIsDecimalPoint = value.lastIndexOf(decimalSeparator) === value.length - 1;
        if (this.props.allowDecimals) {
            return this.formatNumber(this.parseValue(value), 2);
        }
        if (!this.props.isCultureUsedToFormatDisplayValue || !this.isParsable(value) || lastSymbolIsDecimalPoint) {
            return super.getDisplayValue();
        }
        const parsedValue = this.parseValue(value);
        const decimalPrecision = this.getDecimalPrecision(value);
        const displayValue = this.formatNumber(parsedValue, decimalPrecision);
        if (displayValue !== value) {
            return displayValue;
        } else {
            return super.getDisplayValue();
        }
    }

    private getDecimalPrecision(value: string): number {
        const decimalSeparator = Globalize.culture().numberFormat["."];
        const indexOfDecimalSeparator = value.indexOf(decimalSeparator);
        let decimalPrecision = 0;
        if (indexOfDecimalSeparator >= 0) {
            const decimalPart = value.substr(indexOfDecimalSeparator + 1);
            decimalPrecision = decimalPart.length;
        }

        return decimalPrecision;
    }

    private formatNumber(value: number, decimalPrecision: number): string {
        const displayFormatCulture = NumberTextBox._formatCulture;
        const formattedValue = Globalize.format(value, "n" + decimalPrecision, displayFormatCulture);
        return formattedValue;
    }
}
